/** Shared backend-mirror model — TanStack-Query schema as vanilla TypeScript.
 *
 * Layer: `model` per docs/design/spa-account-extraction-260524.md §7 and
 * tools/lint/lint-frontend-layer-imports.py SPA_LAYERS.
 *
 * The runtime synchronization surface between backend-owned state
 * (GCS / Postgres) and frontend views. Pages (`/account`, `/history`)
 * are VIEWS over this model — never sources of truth. Per CLAUDE.md
 * rule #22 ("Genre check before invent"): this module adopts the
 * TanStack Query primitive shape (`queryKey`, status discriminator,
 * optimistic mutation, invalidation tags) but NOT the React runtime.
 * Future migration to TanStack Query (if SPA goes React) is a
 * swap-the-runtime, not a redesign.
 *
 * Per CLAUDE.md rule #11a ("state-bearing → class"): single class
 * instance, single module-level singleton (`backendMirror`).
 *
 * Consumer pattern: see `spa-account/model.ts` (deferred migration —
 * AC.7 establishes the pattern, existing consumers keep inline
 * `authFetch`) and `spa-history/model.ts` (first consumer, AC.7).
 *
 * NOT IN SCOPE for the MVP:
 *   - Background refetch on focus.
 *   - Concurrent-mutation queueing (last-write-wins is fine).
 *   - SSE-driven push state (lives in `spa/job-state.ts` — coexists,
 *     not subsumed; see design doc §7.5).
 */
/** Path-like keys serialize for Map storage. Stable JSON is enough — keys
 *  are tuples of strings/numbers/plain object records (no functions, no
 *  cycles); JSON.stringify is canonical. */
function serializeKey(key) {
    return JSON.stringify(key);
}
/** Returns true when prefix is a prefix of candidate (element-wise). */
function isKeyPrefix(prefix, candidate) {
    if (prefix.length > candidate.length)
        return false;
    for (let i = 0; i < prefix.length; i++) {
        if (JSON.stringify(prefix[i]) !== JSON.stringify(candidate[i]))
            return false;
    }
    return true;
}
export class BackendMirror {
    _cache = new Map();
    _subscribers = new Map();
    _registrations = new Map();
    /** Reverse map: serialized-key → original QueryKey, for prefix invalidation. */
    _knownKeys = new Map();
    /** Register a query: a queryKey + a fetcher function.
     *  Registration is idempotent — re-registering overwrites the fetcher
     *  (allows hot-swapping fetchers in tests). */
    register(key, fetcher, staleTimeMs = 30_000) {
        const s = serializeKey(key);
        this._registrations.set(s, { fetcher: fetcher, staleTimeMs });
        this._knownKeys.set(s, key);
        if (!this._cache.has(s)) {
            this._cache.set(s, { kind: 'idle' });
        }
    }
    /** Read current state synchronously. Returns `{kind: 'idle'}` if the
     *  key is not registered — callers branch on the discriminator. */
    read(key) {
        return this._cache.get(serializeKey(key)) ?? { kind: 'idle' };
    }
    /** Subscribe to a queryKey's status changes. Returns an unsubscribe fn.
     *  Subscribers are called synchronously on every state transition,
     *  including the initial registration if one already exists. */
    subscribe(key, cb) {
        const s = serializeKey(key);
        let set = this._subscribers.get(s);
        if (!set) {
            set = new Set();
            this._subscribers.set(s, set);
        }
        set.add(cb);
        return () => {
            const current = this._subscribers.get(s);
            if (current) {
                current.delete(cb);
                if (current.size === 0)
                    this._subscribers.delete(s);
            }
        };
    }
    /** Force a refetch — sets status to `loading`, executes fetcher, then
     *  transitions to `success` or `error`. If the key isn't registered,
     *  this is a no-op (returns immediately). */
    async invalidate(key) {
        // Prefix invalidation: invalidate all known keys whose tuple starts
        // with `key`. The single-key case (key is a full registered key)
        // is a subset of this.
        const targets = [];
        for (const k of this._knownKeys.values()) {
            if (isKeyPrefix(key, k))
                targets.push(k);
        }
        await Promise.all(targets.map(t => this._refetch(t)));
    }
    /** Execute a mutation. Applies optimistic update if present; rolls back
     *  on error; invalidates the specified keys on success. */
    async mutate(spec, input) {
        let snapshot;
        let optimisticKey;
        if (spec.optimistic) {
            optimisticKey = serializeKey(spec.optimistic.key);
            snapshot = this._cache.get(optimisticKey);
            const currentData = snapshot && snapshot.kind === 'success' ? snapshot.data : null;
            const next = spec.optimistic.produce(currentData, input);
            this._setStatus(spec.optimistic.key, { kind: 'success', data: next, fetchedAt: Date.now() });
        }
        try {
            const result = await spec.perform(input);
            // Invalidate after success — refetches authoritative state.
            await Promise.all(spec.invalidates.map(k => this.invalidate(k)));
            return result;
        }
        catch (err) {
            if (optimisticKey !== undefined && snapshot !== undefined) {
                // Roll back to snapshot — restores authoritative state before
                // the optimistic write.
                this._cache.set(optimisticKey, snapshot);
                this._notify(optimisticKey, snapshot);
            }
            throw err;
        }
    }
    /** Internal: execute the registered fetcher for `key` and propagate
     *  state transitions. Caller-internal — public path is `invalidate`. */
    async _refetch(key) {
        const s = serializeKey(key);
        const reg = this._registrations.get(s);
        if (!reg)
            return;
        this._setStatus(key, { kind: 'loading' });
        try {
            const data = await reg.fetcher();
            this._setStatus(key, { kind: 'success', data, fetchedAt: Date.now() });
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this._setStatus(key, { kind: 'error', error, failedAt: Date.now() });
        }
    }
    _setStatus(key, status) {
        const s = serializeKey(key);
        this._cache.set(s, status);
        this._notify(s, status);
    }
    _notify(serializedKey, status) {
        const subs = this._subscribers.get(serializedKey);
        if (!subs)
            return;
        for (const cb of subs)
            cb(status);
    }
}
/** Module-level singleton — consumers in `spa-account/` and `spa-history/`
 *  share this instance so optimistic updates and invalidations propagate
 *  across trees. */
export const backendMirror = new BackendMirror();
